import {
  AppearanceVariables,
  loadModernTreasury,
  Locale,
} from '@modern-treasury/modern-treasury-js'
import { useEffect, useState } from 'react'

const LOCAL_MOCK_PUBLISHABLE_KEY = 'mock'
const LOCAL_MOCK_HOSTNAMES = new Set([
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
  '::1',
  '[::1]',
  'host.docker.internal',
])

export function isLocalMockHostname(hostname: string): boolean {
  return LOCAL_MOCK_HOSTNAMES.has(hostname) || hostname.endsWith('.nip.io')
}

export type MtEmbeddableFlowOptions = {
  clientToken: string
  /**
   * Appearance theming forwarded to MT's iframe (the only in-iframe styling MT
   * exposes): `colorPrimary`, `colorBackground`, and a `fontFamily` from MT's
   * fixed enum. Sourced from the resolved ext-ui theme so the flow tracks the
   * active brand.
   */
  variables?: AppearanceVariables
  /** MT UI locale (`en` | `es-419` | `auto`). We pass `auto`. */
  locale?: Locale
  onSuccess?: (result: unknown) => void
  onError?: (error: unknown) => void
}

export type MtEmbeddableFlow = {
  mount: (containerSelector: string | HTMLElement) => void
  /** Optional teardown — called on unmount if the instance supports it. */
  unmount?: () => void
}

export type MtInstance = {
  createEmbeddableFlow: (opts: MtEmbeddableFlowOptions) => MtEmbeddableFlow
}

// NOTE on the `declare global Window` augmentation: unlike Chargebee (which
// has no npm loader in this repo — `useChargebeeSdk` injects the
// `<script src="js.chargebee.com/...">` tag and reads `window.Chargebee`
// itself), the real `@modern-treasury/modern-treasury-js` package DOES that
// work for us: `loadModernTreasury()` injects
// `https://cdn.moderntreasury.com/js/v1/modern-treasury.js`, waits for it to
// load, and resolves `window.ModernTreasury(publishableKey)` itself. Its own
// `.d.ts` already augments `Window.ModernTreasury` globally the moment this
// module is imported anywhere in the program, so redeclaring the same global
// here would be redundant (and risks a duplicate/incompatible ambient
// declaration if the two ever drift). This hook never reads
// `window.ModernTreasury` directly — only the imported `loadModernTreasury`
// function — so there is nothing for us to augment.

export type MtSdkStatus = 'loading' | 'ready' | 'error'

type State = {
  status: MtSdkStatus
  mtInstance?: MtInstance
  /**
   * The publishable key `mtInstance` was actually loaded for, set only once
   * `status === 'ready'`. Consumers depend on this stable primitive so a mount
   * effect re-runs when the resolved instance really corresponds to the
   * requested key (org-specific keys / key rotation) — never on the instance's
   * object identity, which is excluded from effect deps by repo convention.
   */
  publishableKey?: string
}

/**
 * Per-publishable-key SDK load state. Keying the cache by the key string (rather
 * than a single set of module globals) is what makes key rotation / org-specific
 * keys safe: a load for key B can never inherit key A's `ready`/instance, and a
 * stale completion for one key can never overwrite another key's entry or be
 * broadcast to a consumer that has since moved to a different key.
 */
type LoadEntry = {
  promise: Promise<void>
  status: MtSdkStatus
  mtInstance?: MtInstance
}

const loadCache = new Map<string, LoadEntry>()

/** Snapshot of the cached load state for `publishableKey` as a hook `State`. */
function stateFor(publishableKey: string): State {
  const entry = loadCache.get(publishableKey)
  if (!entry) {
    return { status: 'loading' }
  }
  return {
    status: entry.status,
    mtInstance: entry.mtInstance,
    // Only surface the resolved key once the instance is actually usable, so a
    // consumer gating its mount on `publishableKey === requestedKey` never
    // mounts against a still-loading or errored entry.
    publishableKey: entry.status === 'ready' ? publishableKey : undefined,
  }
}

/** Loads (or reuses) the real Modern Treasury SDK for `publishableKey`.
 * Keyed by publishable key (unlike Chargebee's cache, which never re-inits
 * once a real instance exists) — a different key must reconstruct the MT
 * object even if the underlying script is already on the page. Two synchronous
 * callers requesting the same key share the one cached entry (and its promise);
 * a stale resolve/reject mutates only its own captured entry, never a newer
 * entry the cache may have replaced it with. */
function loadSdk(publishableKey: string): LoadEntry {
  const existing = loadCache.get(publishableKey)
  if (existing) {
    return existing
  }

  const entry: LoadEntry = {
    status: 'loading',
    // Assigned synchronously just below; the async chain never runs before then.
    promise: undefined as unknown as Promise<void>,
  }
  entry.promise = loadModernTreasury(publishableKey)
    .then((mt) => {
      if (!mt) {
        throw new Error('Modern Treasury SDK failed to load')
      }
      entry.status = 'ready'
      entry.mtInstance = mt as unknown as MtInstance
    })
    .catch((err) => {
      entry.status = 'error'
      // Drop the cached entry so a later retry (a fresh mount, or the same key
      // requested again) can reload instead of being stuck on a rejected
      // promise — but only if this is still the cached entry, so a stale reject
      // can't evict a newer load for the same key.
      if (loadCache.get(publishableKey) === entry) {
        loadCache.delete(publishableKey)
      }
      throw err
    })
  loadCache.set(publishableKey, entry)
  return entry
}

/**
 * Local-only stand-in for the MT embedded flow (gated on `isLocalMockHostname`
 * the same way Chargebee's local mock is). Renders a tiny, non-functional
 * "Enter bank details → Complete" form directly into the mount container (no
 * iframe, no postMessage — there is no real hosted page to point at locally)
 * so onboarding is exercisable without a real publishable key / clientToken.
 */
function createLocalMockInstance(): MtInstance {
  return {
    createEmbeddableFlow: (opts) => {
      let mounted: HTMLElement | null = null
      return {
        mount: (containerSelector): void => {
          const container =
            typeof containerSelector === 'string'
              ? document.querySelector<HTMLElement>(containerSelector)
              : containerSelector

          if (!container) {
            opts.onError?.(
              new Error(
                `Modern Treasury mock container not found: ${containerSelector}`,
              ),
            )
            return
          }

          const wrapper = document.createElement('div')
          wrapper.className = 'mt-local-mock-flow'

          const heading = document.createElement('p')
          heading.textContent = 'Enter bank details (local mock)'
          wrapper.appendChild(heading)

          const fields: Array<[string, string]> = [
            ['mt-mock-routing', 'Routing number'],
            ['mt-mock-account', 'Account number'],
          ]
          fields.forEach(([id, labelText]) => {
            const field = document.createElement('div')
            const label = document.createElement('label')
            label.htmlFor = id
            label.textContent = labelText
            const input = document.createElement('input')
            input.id = id
            input.type = 'text'
            input.setAttribute('autocomplete', 'off')
            field.appendChild(label)
            field.appendChild(input)
            wrapper.appendChild(field)
          })

          const completeButton = document.createElement('button')
          completeButton.type = 'button'
          completeButton.textContent = 'Complete'
          completeButton.addEventListener('click', () => {
            opts.onSuccess?.({ id: 'ext_acct_local_mock' })
          })
          wrapper.appendChild(completeButton)

          container.replaceChildren(wrapper)
          mounted = container
        },
        unmount: (): void => {
          if (mounted) {
            mounted.replaceChildren()
            mounted = null
          }
        },
      }
    },
  }
}

export function __resetForTests(): void {
  loadCache.clear()
}

/**
 * Mirrors `useChargebeeSdk`: a `{ status, mtInstance, publishableKey }` hook
 * backed by a per-key module loader, plus a `publishableKey === 'mock'`
 * local-mock path (gated on `isLocalMockHostname`) so the embedded flow is
 * testable/exercisable on localhost without a real publishable key.
 *
 * Each consumer owns its local state and drives it from its OWN requested key's
 * cache entry, guarding every async completion with a per-effect staleness flag
 * — so a load that resolves after the consumer has moved to a different key can
 * never clobber the newer key's state.
 */
export default function useModernTreasurySdk(cfg: {
  publishableKey: string
}): State {
  // An empty (or mock) key is uninitialized until the effect runs — it must NOT
  // inherit a cached `ready`/instance keyed on some OTHER publishable key (a
  // caller passing '' otherwise saw `status: 'ready'` with no usable instance).
  // A real key reads its own cache entry so an already-loaded key renders
  // `ready` on the first paint without a loading flash.
  const [state, setLocal] = useState<State>(() =>
    cfg.publishableKey && cfg.publishableKey !== LOCAL_MOCK_PUBLISHABLE_KEY
      ? stateFor(cfg.publishableKey)
      : { status: 'loading' },
  )

  useEffect(() => {
    const key = cfg.publishableKey
    if (!key) {
      // No key yet (e.g. the flow-creation call hasn't resolved) — report the
      // uninitialized `loading` state and do not start any SDK load.
      setLocal({ status: 'loading' })
      return
    }

    // A stale flag rather than a shared subscriber list: when the key changes
    // (or the consumer unmounts) this effect's cleanup flips `cancelled`, so a
    // load that resolves for the OLD key is dropped instead of overwriting the
    // new key's state.
    let cancelled = false
    const isCurrent = (): boolean => !cancelled && cfg.publishableKey === key

    if (key === LOCAL_MOCK_PUBLISHABLE_KEY) {
      if (!isLocalMockHostname(window.location.hostname)) {
        setLocal({ status: 'error' })
        return
      }
      setLocal({
        status: 'ready',
        mtInstance: createLocalMockInstance(),
        publishableKey: key,
      })
      return
    }

    // Reflect the current cache state synchronously (already `ready` if this key
    // was loaded earlier), then resolve the load for THIS key.
    setLocal(stateFor(key))

    loadSdk(key)
      .promise.then(() => {
        if (isCurrent()) {
          setLocal(stateFor(key))
        }
      })
      .catch(() => {
        if (isCurrent()) {
          setLocal({ status: 'error' })
        }
      })

    return (): void => {
      cancelled = true
    }
  }, [cfg.publishableKey])

  return state
}
